/*eslint-disable camelcase*/
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Head from 'next/head';
import {
  triggerEnhancedWebviewFeature,
  isBFIslandWebview,
} from '@buzzfeed/bf-utils/lib/webview';
import { useRouter } from 'next/router';

import { CLUSTER } from '../../constants';
import islandMock from '../../constants/island-mock';
import { useTrackingContext } from '../../hooks';
import { canUserInviteFriends } from '../../utils/getUsers';

import styles from './index.module.scss';
import Tabs from './Tabs';
import Item from './Item';
import ChallengeCard from '../CreationLanderShared/ChallengeCard';
import CarouselItem from '../CreationLanderShared/CarouselItem';
import { BFIslandButton, BFIslandColors } from '@buzzfeed/react-components';
import { PlusIcon } from '../../icons/Icons';

// Animation and Floating Button config
const ANIMATION_DURATION_MS = 300;
const SWIPE_THRESHOLD = 50;
const FLOATING_BUTTON_COLORS = [
  BFIslandColors.GREEN_YELLOW,
  BFIslandColors.FUCHSIA,
  BFIslandColors.CYAN,
];

// If user has been away from this page for longer than this, start at the top on return
const SCROLL_RESET_THRESHOLD_MS = 20 * 1000;

// Accent colors for ChallengeCards in Challenges tab
const CHALLENGE_ACCENT_COLORS = ['green-yellow', 'cyan', 'blue-violet'];

const resolveItemType = (item = {}) => {
  if (typeof item?.type === 'string' && item.type.trim()) {
    return item.type;
  }

  const objectType = item?.object_type;
  if (typeof objectType === 'string' && objectType.trim()) {
    return objectType;
  }

  if (objectType && typeof objectType === 'object') {
    const typeCandidates = [objectType.type, objectType.slug, objectType.name];
    for (const candidate of typeCandidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate;
      }
    }
  }

  return undefined;
};

/**
 * Shared component for rendering the creation lander page
 * Used by both /discover (web) and /webview/creation-lander (webview) routes
 */
export default function CreationLanderPage({ page, mockIslandApp, isWeb }) {
  const { trackPageview, trackContentAction, trackInternalLink } =
    useTrackingContext();
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [zones, setZones] = useState(page?.zones || []);

  const [canShowInviteBanner, setCanShowInviteBanner] = useState(false);

  // Swipe gesture and animation handling
  const [dragOffset, setDragOffset] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [enableTransition, setEnableTransition] = useState(false);

  const router = useRouter();

  useEffect(() => {
    if (!isBFIslandWebview()) {
      trackPageview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  useEffect(() => {
    setZones(page?.zones || []);
    setTabIndex(0);
  }, [page]);

  // Enable transitions after initial paint to avoid mount animation,
  // while ensuring the first user-initiated tab change animates.
  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      setEnableTransition(true);
    });

    return () => cancelAnimationFrame(rafId);
  }, []);

  // Only show invite banner if user has remaining invite codes or generations
  useEffect(() => {
    if (isWeb) {
      setCanShowInviteBanner(false);
      return;
    }

    let isMounted = true;

    canUserInviteFriends().then((canInviteFriends) => {
      if (isMounted) {
        setCanShowInviteBanner(canInviteFriends);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [isWeb]);

  // Reset window position to top if user has been away for a while
  const lastHiddenTimestampRef = useRef(null);

  useEffect(() => {
    // Only relevant in native app WebView — the web version has no native navigation
    if (isWeb) return;

    const handleAppEvent = (event) => {
      // The iOS app dispatches 'appEvent' for various actions; we only care about visibility changes
      if (event.detail?.action !== 'visibilityStatus') return;
      if (!('isVisible' in (event.detail?.params || {}))) return;

      const visible = event.detail.params.isVisible;
      if (!visible) {
        // User navigated away — record when they left
        lastHiddenTimestampRef.current = Date.now();
      } else if (lastHiddenTimestampRef.current) {
        // User came back — check how long they were gone
        const elapsed = Date.now() - lastHiddenTimestampRef.current;
        lastHiddenTimestampRef.current = null;

        if (elapsed > SCROLL_RESET_THRESHOLD_MS) {
          window.scrollTo({ top: 0 });
        }
      }
    };

    window.addEventListener('appEvent', handleAppEvent);
    return () => window.removeEventListener('appEvent', handleAppEvent);
  }, [isWeb]);

  // Floating button color, randomize after mount
  const [floatingButtonColorIndex, setFloatingButtonColorIndex] = useState(0);

  useEffect(() => {
    setFloatingButtonColorIndex(
      Math.floor(Math.random() * FLOATING_BUTTON_COLORS.length)
    );
  }, []);

  const zone = zones?.[tabIndex];
  const useLocalApiProxy = useMemo(
    () =>
      ['dev', 'local', 'development'].includes((CLUSTER || '').toLowerCase()),
    []
  );
  const apiBasePath = useLocalApiProxy ? '/api' : '/bf-island-ui-api';

  const loadMore = async (index) => {
    const targetZone = zones?.[index];
    if (!targetZone?.next) {
      return;
    }

    if (isWeb) {
      trackContentAction({
        unit_type: 'component',
        unit_name: targetZone?.name,
        subunit_type: 'feed',
        subunit_name: 'generators',
        item_type: 'button',
        item_name: 'load_more',
        position_in_unit: 1,
        position_in_subunit: null,
        action_type: 'select',
        action_value: 'load_more',
      });
    }

    const parsedUrl = new URL(targetZone.next);
    // use island-api version of routing to get fully island hydrated items
    const pathAndQuery = (parsedUrl.pathname + parsedUrl.search).replace(
      '/feed-api/',
      '/island-api/'
    );

    setLoading(true);

    try {
      const response = await fetch(
        `${apiBasePath}${pathAndQuery}&pageId=${page?.id}`
      );
      const nextZone = await response.json();
      const nextItems = Array.isArray(nextZone?.items) ? nextZone.items : [];

      setZones((prevZones) =>
        prevZones.map((currentZone, idx) => {
          if (idx !== index) {
            return currentZone;
          }
          const existingItems = Array.isArray(currentZone?.items)
            ? currentZone.items
            : [];
          return {
            ...currentZone,
            ...nextZone,
            items: [...existingItems, ...nextItems],
          };
        })
      );
    } catch (error) {
      console.error('Failed to load more items', error);
    } finally {
      setLoading(false);
    }
  };

  // Shared animation function for both swipe and tab click
  const animateToTab = useCallback(
    (newIndex) => {
      if (
        isAnimating ||
        newIndex < 0 ||
        newIndex >= zones.length ||
        newIndex === tabIndex
      )
        return;

      setIsAnimating(true);

      // Calculate animation direction based on tab movement
      // Higher index = move left (like swipe left), Lower index = move right (like swipe right)
      const movingForward = newIndex > tabIndex;
      const exitOffset = movingForward ? -window.innerWidth : window.innerWidth;
      const enterOffset = movingForward
        ? window.innerWidth
        : -window.innerWidth;

      // Animate current items out
      setDragOffset(exitOffset);

      setTimeout(() => {
        setTabIndex(newIndex);
        setEnableTransition(false);
        setDragOffset(enterOffset);

        // Scroll to top while content is off-screen
        window.scrollTo({ top: 0 });

        requestAnimationFrame(() => {
          setEnableTransition(true);
          requestAnimationFrame(() => {
            setDragOffset(0);
            setTimeout(() => {
              setIsAnimating(false);
            }, ANIMATION_DURATION_MS);
          });
        });
      }, ANIMATION_DURATION_MS);
    },
    [isAnimating, zones.length, tabIndex]
  );

  /**
   * Manual touch event handlers for horizontal tab switching.
   *
   * Using @use-gesture/react had reliability issues with gesture detection.
   * The library would prematurely terminate gestures after only 1-2 touchmove events,
   * making tab switching unreliable.
   */
  useEffect(() => {
    const dragContent = document.querySelector(`.${styles.dragContent}`);
    if (!dragContent) return;

    let startX = 0;
    let startY = 0;
    let currentX = 0;
    let isOnCarousel = false;
    let direction = null; // 'horizontal', 'vertical', or null

    const handleTouchStart = (e) => {
      // Skip if touch started on carousel element
      isOnCarousel = e.target.closest('[data-carousel]') !== null;
      if (isOnCarousel) return;

      // Skip if tab animation is in progress
      if (isAnimating) return;

      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      currentX = startX;
      direction = null; // Reset direction for new gesture
    };

    const handleTouchMove = (e) => {
      if (isOnCarousel || isAnimating || !startX) return;

      const touchX = e.touches[0].clientX;
      const touchY = e.touches[0].clientY;
      const deltaX = touchX - startX;
      const deltaY = touchY - startY;

      // Determine direction on first significant movement (10px threshold)
      if (
        direction === null &&
        (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10)
      ) {
        direction =
          Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
      }

      // If vertical scroll, do nothing (let native scroll work)
      if (direction === 'vertical') return;

      // Only handle horizontal movement - show visual feedback
      if (direction === 'horizontal') {
        currentX = touchX;
        const clampedOffset = Math.max(-150, Math.min(150, deltaX));
        setDragOffset(clampedOffset);
      }
    };

    const handleTouchEnd = () => {
      if (isOnCarousel || !startX) return;

      // If vertical scroll, do nothing
      if (direction === 'vertical') {
        startX = 0;
        direction = null;
        return;
      }

      const finalDeltaX = currentX - startX;

      // Check if swipe was long enough to trigger tab change
      if (Math.abs(finalDeltaX) >= SWIPE_THRESHOLD) {
        const newIndex = finalDeltaX < 0 ? tabIndex + 1 : tabIndex - 1;
        if (newIndex >= 0 && newIndex < zones.length) {
          animateToTab(newIndex);
        } else {
          // At first/last tab - snap back
          setDragOffset(0);
        }
      } else {
        // Swipe wasn't far enough - snap back
        setDragOffset(0);
      }

      // Reset state for next gesture
      startX = 0;
      direction = null;
    };

    // Handle interrupted touches (e.g., system gesture, incoming call)
    const handleTouchCancel = () => {
      startX = 0;
      direction = null;
      setDragOffset(0);
    };

    dragContent.addEventListener('touchstart', handleTouchStart, {
      passive: true,
    });
    dragContent.addEventListener('touchmove', handleTouchMove, {
      passive: true,
    });
    dragContent.addEventListener('touchend', handleTouchEnd, { passive: true });
    dragContent.addEventListener('touchcancel', handleTouchCancel, {
      passive: true,
    });

    return () => {
      dragContent.removeEventListener('touchstart', handleTouchStart);
      dragContent.removeEventListener('touchmove', handleTouchMove);
      dragContent.removeEventListener('touchend', handleTouchEnd);
      dragContent.removeEventListener('touchcancel', handleTouchCancel);
    };
  }, [tabIndex, zones.length, isAnimating, animateToTab]);

  const onCreateClick = () => {
    if (!isWeb) {
      trackInternalLink({
        unit_type: 'nav',
        unit_name: 'create',
        item_type: 'button',
        item_name: 'new_generator',
        position_in_unit: 0,
        target_content_type: 'builder',
        target_content_id: 'builder',
      });

      triggerEnhancedWebviewFeature({
        feature: 'navigate',
        params: {
          path: '/webview/widget-builder',
          queryParams: {
            // match what is currently sent from same navigation in app
            source: 'page',
            type: 'creation',
          },
        },
        version: 1,
      });
    }
  };

  const onWebNavigate = (itemType, itemId) => {
    const isLocalhost =
      typeof window !== 'undefined' &&
      window.location.hostname.includes('bf-island-ui.dev.buzzfeed.io');
    const path = isLocalhost
      ? `/content-objects/${itemType}/${itemId}?simulate_island=web`
      : `/generator/${itemId}`;
    router.push(path);
    return;
  };

  return (
    <>
      <Head>
        <meta
          id="viewport"
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1, viewport-fit=cover"
        />
        <meta name="robots" content="noindex, nofollow" />
        {mockIslandApp && (
          <script
            dangerouslySetInnerHTML={{
              __html: `window.BZFD_APP = ${JSON.stringify(islandMock)}`,
            }}
          />
        )}
      </Head>
      <div className={`${styles.creationLander} ${isWeb ? styles.web : ''}`}>
        <Tabs
          zones={zones}
          tabIndex={tabIndex}
          animateToTab={animateToTab}
          dragOffset={dragOffset}
          enableTransition={enableTransition}
          isWeb={isWeb}
          canShowInviteBanner={canShowInviteBanner}
        />
        <div
          className={styles.dragContent}
          style={{
            transform: `translateX(${dragOffset}px)`,
            transition:
              isAnimating && enableTransition
                ? `transform ${ANIMATION_DURATION_MS}ms ease-out`
                : 'none',
          }}
        >
          <div
            className={`${styles.items} ${!canShowInviteBanner ? styles.smallPadding : ''}`}
          >
            {(() => {
              const items = zone?.items || [];
              const packages = items.filter(
                (item) => resolveItemType(item) === 'package'
              );
              const allGenerators = items.filter(
                (item) => resolveItemType(item) !== 'package'
              );

              // Detect Challenges tab for different rendering
              const isChallengesTab =
                zone?.display_name?.toLowerCase() === 'challenges';

              return (
                <>
                  {packages.length > 0 && (
                    <h2 className={styles.sectionHeading}>Trending</h2>
                  )}
                  {packages.map((item, index) => (
                    <Item
                      key={`${tabIndex}-pkg-${index}`}
                      index={index}
                      item={item}
                      resolveItemType={resolveItemType}
                      contentObject={item}
                      isWeb={isWeb}
                      onWebNavigate={onWebNavigate}
                      currentZoneName={zone?.name}
                      zoneOrder={tabIndex}
                    />
                  ))}
                  {allGenerators.length > 0 && !isChallengesTab && (
                    <h2 className={styles.sectionHeading}>All generators</h2>
                  )}
                  {isChallengesTab
                    ? allGenerators.map((item, index) => {
                        const sharedTrackingInfo = {
                          unit_type: 'feed',
                          unit_name: `${zone?.name}|${tabIndex}`,
                          subunit_type: 'component',
                          subunit_name: `bfp_content|${item?.id}`,
                          position_in_unit: index,
                          position_in_subunit: 0,
                        };

                        return (
                          <div
                            key={`${tabIndex}-gen-${index}`}
                            className={styles.challengeCardListLayout}
                          >
                            <ChallengeCard
                              item={item}
                              accentColor={
                                CHALLENGE_ACCENT_COLORS[
                                  index % CHALLENGE_ACCENT_COLORS.length
                                ]
                              }
                              packageType="challenge"
                              isWeb={isWeb}
                              onWebNavigate={onWebNavigate}
                              sharedTrackingInfo={sharedTrackingInfo}
                            />
                          </div>
                        );
                      })
                    : allGenerators.map((item, index) => {
                        if (item.feed_source.name === 'challenges_rev_chron') {
                          // On the "all" tab, ChallengeCards are as part of a package
                          // (subunit = package, `position_in_subunit` is the card index).
                          // On other tabs they appear as standalone items in the feed.
                          const isAllTab = zone?.name === 'all';
                          const standaloneTrackingInfo = {
                            unit_type: 'feed',
                            unit_name: `${zone?.name}|${tabIndex}`,
                            subunit_type: 'component',
                            subunit_name: `${isAllTab ? 'package' : 'bfp_content'}|${item?.id}`,
                            position_in_unit: isAllTab ? 1 : index,
                            position_in_subunit: isAllTab ? index : 0,
                          };

                          return (
                            <div
                              key={`${tabIndex}-carousel-${index}`}
                              className={styles.carouselItemContainer}
                            >
                              <CarouselItem
                                item={item}
                                index={index}
                                packageType="challenge"
                                isWeb={isWeb}
                                isStandaloneCard={true}
                                onWebNavigate={onWebNavigate}
                                currentZoneName={zone?.name}
                                sharedTrackingInfo={standaloneTrackingInfo}
                              />
                            </div>
                          );
                        } else {
                          return (
                            <Item
                              key={`${tabIndex}-gen-${index}`}
                              index={index}
                              item={item}
                              resolveItemType={resolveItemType}
                              contentObject={item}
                              isWeb={isWeb}
                              onWebNavigate={onWebNavigate}
                              currentZoneName={zone?.name}
                              zoneOrder={tabIndex}
                              packagesCount={packages.length}
                            />
                          );
                        }
                      })}
                </>
              );
            })()}
          </div>

          {zone?.next && (
            <BFIslandButton
              onClick={() => loadMore(tabIndex)}
              disabled={loading}
              text="Load More"
              size="medium"
              fullWidth
              className={styles.loadMoreButton}
              loading={loading}
              {...(isWeb
                ? { bgColor: BFIslandColors.GREEN_YELLOW }
                : { bgColor: '#2E2E2E', color: '#ffffff' })}
            />
          )}
        </div>

        {!isWeb && (
          <div className={styles.islandFab}>
            <BFIslandButton
              size="xl"
              iconOnly
              icon={<PlusIcon />}
              onClick={onCreateClick}
              bgColor={FLOATING_BUTTON_COLORS[floatingButtonColorIndex]}
              aria-label="Create"
            />
          </div>
        )}
      </div>
    </>
  );
}